import axios, { AxiosResponse } from 'axios';

import axiosInterceptor from '../../utils/axios';
import { tokenValidator } from '../../utils/tokenValidator';
import { ConfigIProps } from './ordersTypes';

export const beginFileDownload = (
  path: string,
  requestParams: Record<string, unknown>,
  config: ConfigIProps
) => {
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  function poll(fn: any, retries = Infinity, timeoutBetweenAttempts = 1000) {
    return Promise.resolve()
      .then(fn)
      .catch(function retry(err): Promise<unknown> {
        if (retries-- > 0) {
          return delay(timeoutBetweenAttempts).then(fn).catch(retry);
        }
        throw err;
      });
  }

  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + path;
    axiosInterceptor
      .get(url, { params: requestParams })
      .then((response) => {
        let firstFileName = '';
        let nextUrl = '';
        const executionArn = response?.data?.ExecutionArn;
        const pollingParams = {
          ExecutionArn: executionArn,
          DelayInMilliseconds: 1000,
          NumberRetries: 15,
        };
        if (response.data.AuthorizationHeaders) {
          firstFileName = Object.keys(response.data.AuthorizationHeaders)[0];
          nextUrl = response?.data?.AuthorizationHeaders[firstFileName]
            ? response?.data?.AuthorizationHeaders[firstFileName]
            : undefined;
        } else {
          nextUrl = response.data.URL;
        }
        if (nextUrl && !executionArn) {
          downloadFile(response, firstFileName)
            .then(() => {
              // download worked
              resolve(undefined);
            })
            .catch((error) => {
              reject(error.message);
            });
          return;
        } else if (nextUrl && executionArn) {
          const validate = (res: any) => {
            if (res.data.Status === 'FAILED') {
              if (res.data.Error === 'RecordNotFoundRequestException') {
                reject('There are no documents available for download');
              } else {
                reject('Failed to retrieve document. Please try again');
              }
              return;
            }
            if (!res.data || res.data.Status !== 'SUCCEEDED') {
              throw res;
            } else {
              downloadFile(response, firstFileName)
                .then(() => {
                  // download worked
                  resolve(undefined);
                })
                .catch((error) => {
                  reject(error.message);
                });
            }
          };
          return poll(
            () =>
              axiosInterceptor
                .get(config.baseURL + '/order-management/get-workflow-status', {
                  params: pollingParams,
                })
                .then(validate),
            5,
            3000
          );
        } else {
          reject('No presigned url provided');
        }
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const downloadFile = async (
  response: AxiosResponse<any>,
  fileName: string
) => {
  const tokens = await tokenValidator();
  return new Promise((resolve, reject) => {
    axios({
      url: fileName
        ? response.data.AuthorizationHeaders[fileName].URL
        : response.data.URL,
      method: 'GET',
      headers: {
        'X-Bearer-Auth': tokens?.IdToken ?? '',
      },
      responseType: 'blob', // Important
    })
      .then((res) => {
        const link = document.createElement('a');
        link.target = '_self';
        const blob = new Blob([res.data], { type: 'application/pdf' });
        link.href = URL.createObjectURL(blob);
        if (fileName) {
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
          window.close();
        } else {
          link.click();
        }
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
